<script setup lang="ts">
import { ref, onUnmounted } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

interface Props {
  modelValue?: File[];
  disabled?: boolean;
  maxFiles?: number;
  maxSizeMb?: number;
  // D-37 / B1: optional upload-progress map keyed by File instance. Files
  // present with a value < 100 render the uploading tile state; otherwise
  // the done tile state. Parent orchestrates the upload + progress updates.
  uploadProgress?: Map<File, number>;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  disabled: false,
  maxFiles: 10,
  maxSizeMb: 20,
  uploadProgress: () => new Map<File, number>(),
});

const emit = defineEmits<{
  'update:modelValue': [files: File[]];
  cancel: [file: File];
}>();

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
// REVIEW: regex fallback for browsers reporting empty MIME or 'image/heic-sequence'
const ALLOWED_EXT_REGEX = /\.(heic|heif|jpe?g|png)$/i;

const uploadError = ref<string>('');
const fileInput = ref<HTMLInputElement | null>(null);

function isAllowed(file: File): boolean {
  return ALLOWED_TYPES.includes(file.type) || ALLOWED_EXT_REGEX.test(file.name);
}

function isUnderSize(file: File): boolean {
  return file.size <= props.maxSizeMb * 1024 * 1024;
}

function processFiles(incoming: File[]): void {
  uploadError.value = '';
  const valid: File[] = [];
  for (const f of incoming) {
    if (!isAllowed(f)) {
      uploadError.value = 'Only JPG, PNG, and HEIC files are allowed.';
      continue;
    }
    if (!isUnderSize(f)) {
      uploadError.value = `Files must be under ${props.maxSizeMb}MB.`;
      continue;
    }
    valid.push(f);
  }
  emit(
    'update:modelValue',
    [...props.modelValue, ...valid].slice(0, props.maxFiles),
  );
}

function onFileChange(e: Event): void {
  const input = e.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  processFiles(files);
  // WR-05: clear the input so re-selecting the same file fires @change.
  // Without this, removing a file and re-picking the SAME file is a silent
  // no-op (browsers suppress duplicate-value change events). Matches
  // VideoUpload.onFileChange.
  input.value = '';
}

function onDrop(e: DragEvent): void {
  const files = Array.from(e.dataTransfer?.files ?? []);
  processFiles(files);
}

function openPicker(): void {
  fileInput.value?.click();
}

function removeFile(index: number): void {
  // WR-06: revoke the object URL for the removed file so it does not leak
  // in `objectUrls` until route unmount. Previously, removeFile only
  // filtered modelValue, leaving the stale URL in the map.
  const removed = props.modelValue[index];
  const next = props.modelValue.filter((_, i) => i !== index);
  if (removed) {
    const url = objectUrls.get(removed);
    if (url) {
      URL.revokeObjectURL(url);
      objectUrls.delete(removed);
    }
  }
  emit('update:modelValue', next);
}

// D-37 / B1: tile state predicates.
function isUploading(file: File): boolean {
  const p = props.uploadProgress.get(file);
  return typeof p === 'number' && p < 100;
}

function progressOf(file: File): number {
  return props.uploadProgress.get(file) ?? 0;
}

function onCancel(file: File): void {
  emit('cancel', file);
  // WR-06: revoke the object URL for the cancelled file so it does not leak.
  const url = objectUrls.get(file);
  if (url) {
    URL.revokeObjectURL(url);
    objectUrls.delete(file);
  }
  const next = props.modelValue.filter((f) => f !== file);
  emit('update:modelValue', next);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const objectUrls = new Map<File, string>();

function thumbUrl(file: File): string {
  if (!objectUrls.has(file)) {
    objectUrls.set(file, URL.createObjectURL(file));
  }
  return objectUrls.get(file)!;
}

onUnmounted(() => {
  objectUrls.forEach((url) => URL.revokeObjectURL(url));
  objectUrls.clear();
});
</script>

<template>
  <div>
    <div
      class="border-2 border-dashed border-border-strong rounded-lg p-6 text-center cursor-pointer flex items-center justify-center min-h-24"
      :class="disabled ? 'opacity-50 cursor-not-allowed' : ''"
      role="button"
      tabindex="0"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <p class="text-base text-muted">Drop photos here or click to upload</p>
      <input
        ref="fileInput"
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        class="hidden"
        :disabled="disabled"
        @change="onFileChange"
      />
    </div>
    <p class="text-sm text-muted mt-2">
      Up to {{ maxFiles }} photos, JPG or PNG, {{ maxSizeMb }}MB each.
    </p>
    <p v-if="uploadError" class="text-sm text-danger mt-2" role="alert">
      {{ uploadError }}
    </p>
    <div
      v-if="modelValue.length > 0"
      class="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4"
    >
      <template v-for="(file, i) in modelValue" :key="`${file.name}-${i}`">
        <!-- D-37 / B1: uploading tile -->
        <div
          v-if="isUploading(file)"
          data-testid="upload-tile-uploading"
          class="relative rounded-md border border-border bg-surface p-2 flex flex-col gap-1"
        >
          <img
            :src="thumbUrl(file)"
            :alt="`Uploading photo ${i + 1} of ${modelValue.length}`"
            class="w-full aspect-square object-cover rounded-md opacity-75"
            data-testid="upload-tile-icon"
          />
          <span
            data-testid="upload-tile-filename"
            class="text-xs break-all line-clamp-2 pr-5 text-foreground"
            >{{ file.name }}</span
          >
          <span data-testid="upload-tile-size" class="text-xs text-muted">{{
            formatBytes(file.size)
          }}</span>
          <div
            data-testid="upload-tile-progress"
            role="progressbar"
            :aria-valuenow="progressOf(file)"
            aria-valuemin="0"
            aria-valuemax="100"
            class="h-1.5 bg-border rounded-full overflow-hidden"
          >
            <div
              class="h-full bg-primary transition-all"
              :style="{ width: `${progressOf(file)}%` }"
            />
          </div>
          <button
            type="button"
            data-testid="upload-tile-cancel"
            aria-label="Cancel upload"
            class="absolute top-1 right-1 bg-black/50 text-white rounded-full size-5 flex items-center justify-center text-xs"
            @click="onCancel(file)"
          >
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>
        <!-- D-37 / B1: done tile -->
        <div
          v-else
          data-testid="upload-tile-done"
          class="relative rounded-md border border-border bg-surface p-2 flex flex-col gap-1"
        >
          <img
            :src="thumbUrl(file)"
            :alt="`Appointment photo ${i + 1} of ${modelValue.length}`"
            class="w-full aspect-square object-cover rounded-md"
            data-testid="upload-tile-icon"
          />
          <span
            data-testid="upload-tile-filename"
            class="text-xs break-all line-clamp-2 pr-5 text-foreground"
            >{{ file.name }}</span
          >
          <span data-testid="upload-tile-size" class="text-xs text-muted">{{
            formatBytes(file.size)
          }}</span>
          <button
            type="button"
            data-testid="upload-tile-delete"
            aria-label="Remove file"
            class="absolute top-1 right-1 bg-black/50 text-white rounded-full size-5 flex items-center justify-center text-xs"
            @click="removeFile(i)"
          >
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
